import React from 'react';

// Importing dependencies
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useSelector } from "react-redux";
import { Toaster } from 'react-hot-toast';

// Importing scenes
import Layout from './scenes/layout';
import Login from './scenes/login';
import Dashboard from './scenes/dashboards';
import EDocs from './scenes/edocs';
import EmployeeDetails from './scenes/employee';
import PageNotFound from './scenes/404';

// Import Utility
import { isAuthenticated, getAllowedPermissions, isAuthenticatedRoleAdmin, isAuthenticatedRoleTeamWise, } from './utils/authentication';
import allowedRoutes from './components';
import { isAllowedToDoByKey } from './permissions-hub';
import { EMPLOYEE_MANAGEMENT, ACCEPTED_PERMISSIONS, PAYROLLNOTE_MANAGEMENT, ANALYTICS_MANAGEMENT } from "./constants";

const App = () => {
  // const { userInfo: loggedUserInfo, userToken } = useSelector((state) => state.auth);
  const { userInfo: loggedUserInfo, } = useSelector((state) => state.auth);

  const authenticated = isAuthenticated( loggedUserInfo ); // isAuthenticated( userToken ); 
  
  const userPermissions = getAllowedPermissions( loggedUserInfo );

  const isRoleAdmin = isAuthenticatedRoleAdmin( loggedUserInfo );

  const isRoleTeamWise = isAuthenticatedRoleTeamWise( loggedUserInfo );

  // Check update permission
  const isAllowedToAccessEmployeeDetails = isAllowedToDoByKey(
    EMPLOYEE_MANAGEMENT,
    ACCEPTED_PERMISSIONS["read"],
    getAllowedPermissions(loggedUserInfo)
  );
  
  const isAllowedToUpdatePayrollNotes = isAllowedToDoByKey(
    PAYROLLNOTE_MANAGEMENT,
    ACCEPTED_PERMISSIONS["update"],
    getAllowedPermissions(loggedUserInfo),
    false
  );

  return (
    <BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#4aed88',
              secondary: '#fff',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#ff6b6b',
              secondary: '#fff',
            },
          },
        }}
      />
      <Routes>
        <Route path="/login" element={<Login />} />
        {authenticated ? (
          <>
            <Route path="/" element={<Layout />}>
              <Route index element={<Dashboard />} />
              <Route path="/edocs" element={<EDocs permissions={['read']} />} />
              {Object.entries(userPermissions).map(([key, permissions]) => {
                
                const isComponent = permissions?.isComponent;
                
                if (isComponent) {
                  const routePermissions = permissions?.routes;
                  
                  // Map old "sheet_management" key to new "setting_management" for backward compatibility
                  const routeKey = key === 'sheet_management' ? 'setting_management' : key;
                                    
                  // Check if the allowedRoutes object has the key
                  if (allowedRoutes[routeKey]) {
                    const Component = allowedRoutes[routeKey]["component"];
                    const path = allowedRoutes[routeKey]["route"];

                    const props = {
                      ...allowedRoutes[routeKey]["props"],
                      permissions: routePermissions,
                      isRoleAdmin,
                      isRoleTeamWise,
                    };

                    // To display payroll notes component in analytics page
                    if (key === ANALYTICS_MANAGEMENT) {
                      props.isAllowedToUpdatePayrollNotes = isAllowedToUpdatePayrollNotes;
                    }

                    return (
                      <Route
                        key={key}
                        path={path}
                        element={<Component {...props} />}
                      />
                    );
                  }
                }

                return null;
              })}
              {isAllowedToAccessEmployeeDetails && (<Route path="/employee/:employeeId" element={<EmployeeDetails />} />)}
            </Route>
          </>
        ) : (
          <Route path="/" element={<Navigate to="/login" replace />} />
        )}
        <Route path='*' element={<PageNotFound />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
